import { useMemo, useEffect, useCallback, FunctionComponent } from "react";

import Stack from "@mui/material/Stack";
import {
  FormCategory,
  getDonationOrTicketingFormLink,
  getDollarValue,
  formatCurrencyAmount,
  formatPaymentMethod,
  getField,
  isBankDebitPaymentMethod,
  pascalCaseToCamelCase,
  PaymentMethod,
  Utils,
} from "@simplyk/common";
import { useSnackbar } from "notistack";

import { Header } from "../Header/Header";
import { MembershipSection } from "../MembershipSection/MembershipSection";
import { NonprofitSection } from "../NonprofitSection/NonprofitSection";
import { PaymentDetails } from "../PaymentDetails/PaymentDetails";
import { Tickets } from "../Tickets/Tickets";
import { DonorPaymentDetailDrawerProps, useDonorPaymentDetailDrawer } from "../useDonorPaymentDetailDrawer";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { PaymentDetailCard } from "@/components/PaymentDetailCard/PaymentDetailCard";
import { DashboardDivider } from "@/dashboard-design-system/Divider/DashboardDivider";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton";
import { Card, Heart } from "@/icons/outlined";

type DonorPaymentDetailContentProps = Omit<DonorPaymentDetailDrawerProps, "onClose"> & {
  onClose?: () => void;
  viewedFrom: "DonorSpace" | "DonorAssistant";
};

export const DonorPaymentDetailContent: FunctionComponent<DonorPaymentDetailContentProps> = (props) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { enqueueSnackbar } = useSnackbar();
  const { locale, isoLocale } = useLocaleContext();

  const {
    payment,
    command,
    form,
    subscription,
    receipt,
    organization,
    isLoading,
    updateCreditCard,
    clickOnDownloadReceipt,
    clickDownloadETicket,
    isActiveSubscription,
    editDonationAmount,
    editDonationAmountIsLoading,
    editChargeDate,
    editChargeDateIsLoading,
    scheduleSubscriptionEnd,
    scheduleSubscriptionEndIsLoading,
    cancelSubscription,
    cancelSubscriptionIsLoading,
    isMembership,
    membershipStatus,
    ticketCancellationStatus,
    hasAutomaticRenewal,
    activeProductTickets,
    adjustTipLink,
    isRecurringPayment,
  } = useDonorPaymentDetailDrawer(props);

  useEffect(() => {
    if (props.isOpen) {
      logAmplitudeEvent(AmplitudeEvents.PaymentDetailsViewed, { from: props.viewedFrom, platform: "web" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const organizationName = organization?.name;
  const organizationCountry = organization?.country;
  const contactEmail = organization?.contactEmail ?? undefined;

  const isRecurring = Boolean(payment?.subscriptionId);
  const paymentMethodLabel = useMemo(() => {
    if (!payment?.paymentMethod) {
      return "";
    }
    if (isBankDebitPaymentMethod(payment.paymentMethod)) {
      return formatPaymentMethod({ locale, paymentMethod: payment.paymentMethod, last4: payment.last4 });
    }
    if (payment.paymentMethod === PaymentMethod.Cheque) {
      return formatPaymentMethod({ locale, paymentMethod: payment.paymentMethod, country: organizationCountry });
    }
    if (payment.last4) {
      return t("dashboard", "donorTransactionDetailDrawer.cardEndingIn", { last4: payment.last4 });
    }
    return t("dashboard", `transactionlist.${pascalCaseToCamelCase(payment.paymentMethod)}` as never);
  }, [payment?.paymentMethod, payment?.last4, locale, organizationCountry, t]);

  const formName = useMemo(() => {
    return form?.fields ? getField(form?.fields, locale)?.title || "" : "";
  }, [locale, form]);

  const formLink = useMemo(() => {
    if (!form || !props.content?.formType) {
      return "";
    }
    return getDonationOrTicketingFormLink({
      form: {
        id: form.id,
        path: form.path,
        formType: props.content.formType,
        formCategory: form.formCategory as unknown as FormCategory,
        isPrimaryCampaign: form.id === form.campaignId,
        isPrimaryTeam: undefined,
      },
      isoLocale,
    });
  }, [form, props.content?.formType, isoLocale]);

  const tipAmount = payment?.tip ?? 0;
  const donationAmount = subscription
    ? subscription.totalAmount - subscription.tipAmount
    : payment
      ? payment.transferrableAmount - tipAmount
      : 0;

  const handleEditAmount = useCallback(
    async (newAmount: number) => {
      if (!props.content?.commandId) {
        return false;
      }
      return editDonationAmount(props.content.commandId, newAmount);
    },
    [editDonationAmount, props.content?.commandId]
  );

  const formattedTip = payment
    ? formatCurrencyAmount({
        value: getDollarValue(tipAmount),
        locale: isoLocale,
        currency: payment.currency,
      })
    : "";

  const formattedAmount = payment
    ? formatCurrencyAmount({
        value: getDollarValue(payment.transferrableAmount),
        locale: isoLocale,
        currency: payment.currency,
      })
    : "";

  const ratesMap = useMemo(() => Utils.arrayToIdMap(form?.rates || []), [form?.rates]);

  const handleCopyEmail = useCallback(() => {
    if (contactEmail) {
      void navigator.clipboard.writeText(contactEmail);
      enqueueSnackbar(t("dashboard", "common.emailCopied"), { vibe: "positive" });
    }
  }, [contactEmail, enqueueSnackbar, t]);

  const membershipChipVibe = useMemo(() => {
    if (membershipStatus === "active") {
      return "positive" as const;
    }
    if (membershipStatus === "expiringSoon") {
      return "warning" as const;
    }
    return "danger" as const;
  }, [membershipStatus]);

  const membershipChipLabel = useMemo(() => {
    if (membershipStatus === "active") {
      return t("dashboard", "donorTransactionDetailDrawer.membershipActive");
    }
    if (membershipStatus === "expiringSoon") {
      return t("dashboard", "donorTransactionDetailDrawer.membershipExpiringSoon");
    }
    return t("dashboard", "donorTransactionDetailDrawer.membershipExpired");
  }, [membershipStatus, t]);

  const membershipTypeName = useMemo(() => {
    if (!activeProductTickets?.length) {
      return "";
    }
    const rate = ratesMap[activeProductTickets[0].rateId];
    const field = rate?.fields && getField(rate.fields, locale);
    return field?.title || "";
  }, [activeProductTickets, ratesMap, locale]);

  const membersByProductTicketId = useMemo(
    () => Object.fromEntries((command?.ticketMembers ?? []).map((member) => [member.productTicketId, member])),
    [command?.ticketMembers]
  );

  if (!props.content || !payment || isLoading) {
    return <DashboardSkeleton variant="rectangular" height="100%" width="100%" />;
  }

  return (
    <Stack spacing={3} sx={{ pb: "100px" }}>
      <Header
        payment={payment}
        formattedAmount={formattedAmount}
        isMembership={isMembership}
        isSweepstakes={isSweepstakesCategory(form?.formCategory)}
        ticketCancellationStatus={ticketCancellationStatus}
        receipt={receipt}
        command={command}
        clickOnDownloadReceipt={clickOnDownloadReceipt}
        clickDownloadETicket={clickDownloadETicket}
        onClose={props.onClose}
        onEditAmount={isRecurring && isActiveSubscription ? handleEditAmount : undefined}
        editAmountIsLoading={editDonationAmountIsLoading}
        currentDonationAmount={donationAmount || undefined}
        initialControl={props.initialControl}
      />

      <DashboardDivider />

      {isMembership && membershipStatus ? (
        <MembershipSection
          membershipChipLabel={membershipChipLabel}
          membershipChipVibe={membershipChipVibe}
          membershipTypeName={membershipTypeName}
          createdAtUtc={payment.createdAtUtc}
          organizationName={organizationName || ""}
          ownerEmail={contactEmail}
          onCopyEmail={handleCopyEmail}
          formLink={formLink}
          formName={formName}
        />
      ) : (
        <NonprofitSection
          organizationName={organizationName || ""}
          ownerEmail={contactEmail}
          onCopyEmail={handleCopyEmail}
          formLink={formLink}
          formName={formName}
        />
      )}

      <DashboardDivider />

      <PaymentDetails
        paymentMethodLabel={paymentMethodLabel}
        tipAmount={tipAmount}
        formattedTip={formattedTip}
        isRecurring={isRecurring}
        isActiveSubscription={isActiveSubscription}
        payment={payment}
        command={command}
        subscription={subscription}
        organizationCountry={organizationCountry}
        formLink={formLink}
        adjustTipLink={adjustTipLink}
        isRecurringPayment={isRecurringPayment}
        cancelSubscription={cancelSubscription}
        cancelSubscriptionIsLoading={cancelSubscriptionIsLoading}
        editChargeDate={editChargeDate}
        editChargeDateIsLoading={editChargeDateIsLoading}
        scheduleSubscriptionEnd={scheduleSubscriptionEnd}
        scheduleSubscriptionEndIsLoading={scheduleSubscriptionEndIsLoading}
        formattedAmount={formattedAmount}
        organizationName={organizationName || ""}
        updateCreditCard={updateCreditCard}
        hasGiftAidConfirmation={command?.hasGiftAidConfirmation}
        hasAutomaticRenewal={hasAutomaticRenewal}
        isMembership={isMembership}
        initialControl={props.initialControl}
      />

      {!!payment.extraDonation && (
        <>
          <DashboardDivider />
          <PaymentDetailCard
            title={t("dashboard", "paymentsPage.table.detailsDrawer.extraDonation")}
            amount={payment.extraDonation}
            paymentCurrency={payment.currency}
            icon={<Heart />}
          />
        </>
      )}

      {command?.productTickets && command.productTickets.length > 0 && (
        <>
          <DashboardDivider />
          <Tickets
            productTickets={command.productTickets}
            ratesMap={ratesMap}
            currency={payment.currency}
            isSweepstakes={isSweepstakesCategory(form?.formCategory)}
            membersByProductTicketId={membersByProductTicketId}
            icon={isMembership ? <Card /> : undefined}
          />
        </>
      )}
    </Stack>
  );
};
